/**
 * Utilities for building the shell bundle API payload (POST /v1/config-tools/sync/create-shell-bundle).
 * New format: single object with projectId, sourceEnvId, targetEnvironments, items.
 * Each item has targets array with one entry per target env: { envId, targetValue, oldValue, status }.
 */

type TargetEnvironmentTe = {
  id: number;
  environmentName?: string;
  environment_name?: string;
  type?: string;
  stackName?: string;
  stack_name?: string;
};

export type ShellBundleTargetEnv = { id: number; name: string };

export type ShellBundlePayload = {
  projectId: string;
  sourceEnvId: number;
  targetEnvironments: ShellBundleTargetEnv[];
  items: Array<{
    category: string;
    key: string;
    sourceValue: any;
    targets: Array<{ envId: number; targetValue: string; oldValue?: string; status?: string }>;
    metadata?: Record<string, any>;
  }>;
};

/** Strip leading configType:: prefix from a composite key to get the bare configKey. */
function extractBareConfigKey(compositeKey: string, configType?: string): string {
  const k = compositeKey.trim();
  if (configType) {
    const prefix = `${configType.trim()}::`;
    if (k.startsWith(prefix)) return k.slice(prefix.length);
  }
  if (k.includes('::')) return k.split('::').slice(1).join('::');
  return k;
}

function safeStringify(v: any): string {
  if (v === null || typeof v === 'undefined') return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

/**
 * Derive short name for BE zip filenames (e.g. nonprod-configs-77777.sh).
 * Uses first segment from environmentName before underscore, lowercased, or type as fallback.
 */
export function deriveEnvName(te: TargetEnvironmentTe): string {
  const envName = String(te.environmentName ?? te.environment_name ?? '').trim();
  if (envName) {
    const first = envName.split('_')[0];
    if (first) return first.toLowerCase();
  }
  const type = String(te.type ?? '').trim().toLowerCase();
  if (type) return type;
  return `env${te.id}`;
}

/**
 * Build shell bundle payload in new API format.
 * Converts FE items (per-env, envValues, targets, or single-target) to unified items with targets array.
 */
export function buildShellBundlePayload(
  items: any[],
  targetEnvironments: TargetEnvironmentTe[],
  sourceEnvId: number,
  projectId: string
): ShellBundlePayload {
  const targetEnvsForApi: ShellBundleTargetEnv[] = targetEnvironments.map((te) => ({
    id: Number(te.id),
    name: deriveEnvName(te),
  }));

  const itemsByKey = new Map<
    string,
    {
      category: string;
      key: string;
      rawKey: string;
      sourceValue: any;
      metadata?: Record<string, any>;
      envValues: Map<number, { targetValue: string; oldValue: string; status: string }>;
    }
  >();

  const getItemKey = (item: any) => {
    const category = String(
      item.category ??
      item.metadata?.category ??
      'configs'
    );
    let configType = item.metadata?.configType != null && String(item.metadata.configType).trim() !== ''
      ? String(item.metadata.configType).trim()
      : '';
    if (!configType && typeof item.key === 'string' && item.key.includes('::')) {
      configType = item.key.split('::')[0].trim();
    }
    const rawKey = String(
      item.metadata?.configKey ??
      item.key ??
      item.configKey ??
      item.metadata?.key ??
      (item.itemId != null ? String(item.itemId) : '')
    );
    const key = extractBareConfigKey(rawKey, configType);
    const meta = item.metadata && typeof item.metadata === 'object' ? item.metadata : {};
    const productType = String(meta.productType ?? (meta as any).product_type ?? '').trim();
    const fuelType = String(meta.fuelType ?? (meta as any).fuel_type ?? '').trim();
    const clusterRef = String(meta.clusterReference ?? (meta as any).cluster_reference ?? '').trim();
    const parts = [category, configType || '_', key, productType || '_', fuelType || '_', clusterRef || '_'];
    return parts.join('::');
  };

  const upsertEnvValue = (
    mapKey: string,
    category: string,
    key: string,
    rawKey: string,
    sourceValue: any,
    metadata: Record<string, any> | undefined,
    envId: number,
    targetValue: string,
    status: string,
    oldValue?: string
  ) => {
    if (!itemsByKey.has(mapKey)) {
      itemsByKey.set(mapKey, {
        category,
        key,
        rawKey,
        sourceValue,
        metadata: metadata ? { ...metadata } : undefined,
        envValues: new Map(),
      });
    }
    const data = itemsByKey.get(mapKey)!;
    // Same config can appear multiple times (e.g. one item per target env). Merge metadata from every
    // occurrence so we never drop keys (e.g. tags on string resources) that might only be on a later item.
    if (metadata && typeof metadata === 'object' && Object.keys(metadata).length > 0) {
      data.metadata = { ...(data.metadata || {}), ...metadata };
    }
    data.envValues.set(envId, { targetValue, oldValue: oldValue ?? '', status });
  };

  for (const item of items) {
    // Extract category - try multiple possible fields
    const category = String(
      item.category ?? 
      item.metadata?.category ?? 
      (item.itemId != null ? 'configs' : 'configs')
    );
    
    // Extract configType first so we can strip it from composite keys
    const itemConfigType = item.metadata?.configType != null && String(item.metadata.configType).trim() !== ''
      ? String(item.metadata.configType).trim()
      : (typeof item.key === 'string' && item.key.includes('::') ? item.key.split('::')[0].trim() : '');

    // Extract key - prefer metadata.configKey (bare), else derive from composite key by splitting
    const rawKeyCandidate = String(
      item.metadata?.configKey ?? 
      item.key ?? 
      item.configKey ??
      item.metadata?.key ??
      (item.itemId != null ? String(item.itemId) : '')
    );
    const key = extractBareConfigKey(rawKeyCandidate, itemConfigType);
    
    if (!key || key.trim() === '') {
      console.warn('Skipping item with empty key:', item);
      continue;
    }
    
    const rawKey = String(item.key ?? '');
    const mapKey = getItemKey(item);
    const sourceValue = item.sourceValue ?? item.source_value ?? '';
    const metadata = item.metadata && typeof item.metadata === 'object' ? item.metadata : undefined;

    if ((item as any).envId != null) {
      const envId = Number((item as any).envId);
      const targetValue = safeStringify(item.targetValue ?? item.target_value ?? '');
      const oldValue = safeStringify((item as any).oldValue ?? (item as any).old_value ?? '');
      const status = String(item.status ?? 'unchanged').toLowerCase();
      upsertEnvValue(mapKey, category, key, rawKey, sourceValue, metadata, envId, targetValue, status, oldValue);
    } else if (
      item.metadata?.isEnvVar === true &&
      (item as any).envValues &&
      typeof (item as any).envValues === 'object'
    ) {
      const envValues = (item as any).envValues;
      if (envValues instanceof Map) {
        envValues.forEach((val: any, envId: number) => {
          const targetValue = safeStringify(val);
          upsertEnvValue(mapKey, category, key, rawKey, sourceValue, metadata, Number(envId), targetValue, 'unchanged');
        });
      } else {
        const obj = envValues as Record<string | number, any>;
        Object.entries(obj).forEach(([k, v]) => {
          const envId = Number(k);
          if (!Number.isNaN(envId)) {
            const targetValue = safeStringify(v);
            upsertEnvValue(mapKey, category, key, rawKey, sourceValue, metadata, envId, targetValue, 'unchanged');
          }
        });
      }
    } else if (Array.isArray(item.targets) && item.targets.length > 0) {
      for (const t of item.targets) {
        // Support both nested (targetEnv.id) and flat (envId/env_id) structures
        const tg = t.targetEnv;
        const rawEnvId = tg?.id ?? tg?.envId ?? t?.envId ?? t?.env_id ?? t?.id;
        const envId = typeof rawEnvId === 'number' ? rawEnvId : Number(rawEnvId);
        if (!Number.isNaN(envId)) {
          const targetValue = safeStringify(t.targetValue ?? t.target_value ?? '');
          const oldValue = safeStringify((t as any).oldValue ?? (t as any).old_value ?? '');
          const status = String(t.status ?? 'unchanged').toLowerCase();
          upsertEnvValue(mapKey, category, key, rawKey, sourceValue, metadata, envId, targetValue, status, oldValue);
        }
      }
    } else {
      const targetValue = safeStringify(item.targetValue ?? item.target_value ?? '');
      const status = String(item.status ?? 'unchanged').toLowerCase();
      for (const te of targetEnvironments) {
        const envId = Number(te.id);
        upsertEnvValue(mapKey, category, key, rawKey, sourceValue, metadata, envId, targetValue, status);
      }
    }
  }

  const unifiedItems = Array.from(itemsByKey.entries()).map(([, data]) => {
    const targets = targetEnvsForApi.map((te) => {
      const entry = data.envValues.get(te.id);
      return {
        envId: te.id,
        targetValue: entry?.targetValue ?? '',
        oldValue: entry?.oldValue ?? '',
        status: entry?.status ?? 'unchanged',
      };
    });
    // Backend expects key as "configType::configKey" for configs; string resources use item key only.
    const configType = data.metadata?.configType != null && String(data.metadata.configType).trim() !== ''
      ? String(data.metadata.configType).trim()
      : (data.rawKey && data.rawKey.includes('::') ? data.rawKey.split('::')[0].trim() : '');
    const bareKey = data.metadata?.configKey != null && String(data.metadata.configKey).trim() !== ''
      ? extractBareConfigKey(String(data.metadata.configKey).trim(), configType)
      : (data.rawKey && data.rawKey.includes('::')
          ? extractBareConfigKey(data.rawKey, configType)
          : data.key);
    let keyForPayload: string;
    if (configType && bareKey) {
      keyForPayload = `${configType}::${bareKey}`;
    } else {
      keyForPayload = bareKey || data.key;
    }
    // Pass metadata as-is (compare API already sends tags as comma-separated string). String resources: stringKey, section, tags; configs: configType, configKey (no tags).
    return {
      category: data.category,
      key: keyForPayload,
      sourceValue: safeStringify(data.sourceValue),
      targets,
      ...(data.metadata && Object.keys(data.metadata).length > 0 ? { metadata: { ...data.metadata } } : {}),
    };
  });

  return {
    projectId: String(projectId),
    sourceEnvId: Number(sourceEnvId),
    targetEnvironments: targetEnvsForApi,
    items: unifiedItems,
  };
}
